import { NavLink, Outlet, useLocation, Link } from 'react-router';
import { SECTION_LABELS, type AnalyticsSection } from '#shared/types';
import { Page } from '#client/components/ui/Page';
import { ChartDefs } from '#client/components/charts';
import { useCan } from '#client/hooks/useCan';
import type { Permission } from '#shared/types';
import { routes } from '#client/lib/routes';

/**
 * The shell every analytics section sits in.
 *
 * ## Why the section bar is links and not `Tabs`
 *
 * `Tabs` renders `role="tablist"`, and a tablist tells a screen reader that the
 * panels are in this document and that arrow keys move between them. Neither is
 * true here: each section is a route with its own data. Using it would be an
 * ARIA lie that makes the component *less* usable to the people it exists for.
 * A `<nav>` of `NavLink`s is what this is, so that is what it says.
 *
 * ## Why the links carry the query string
 *
 * Switching from Volume to Flow must not silently reset the fortnight somebody
 * just selected. Each link copies the current search string; anything the target
 * section does not understand is dropped server-side by `dimensionFor`, so a
 * `by=hour` cannot be smuggled onto a screen with no hours.
 */

interface SectionLink {
  section: AnalyticsSection;
  to: string;
  capability?: Permission;
}

const SECTIONS: SectionLink[] = [
  { section: 'overview', to: routes.analytics },
  { section: 'volume', to: routes.analyticsVolume },
  { section: 'patients', to: routes.analyticsPatients },
  { section: 'flow', to: routes.analyticsFlow },
  { section: 'quality', to: routes.analyticsQuality },
  { section: 'staff', to: routes.analyticsStaff, capability: 'analytics.staff' },
  { section: 'supply', to: routes.analyticsSupply, capability: 'analytics.supply' },
];

export function AnalyticsLayout() {
  const can = useCan();
  const { search } = useLocation();

  const visible = SECTIONS.filter((link) => !link.capability || can(link.capability));

  return (
    <Page title="Analytics" subtitle="How the lab is running, and how that is changing" fullWidth>
      {/* Mounted once here rather than inside each chart: SVG pattern ids are
          document-global, so N charts each defining them would leave the last
          one mounted winning for all of them. */}
      <ChartDefs />

      <nav className="c-an-nav" aria-label="Analytics sections">
        {visible.map((link) => (
          <NavLink
            key={link.section}
            to={{ pathname: link.to, search }}
            end={link.section === 'overview'}
            className={({ isActive }) => (isActive ? 'c-an-nav__link is-active' : 'c-an-nav__link')}
          >
            {SECTION_LABELS[link.section]}
          </NavLink>
        ))}

        {/* Revenue lives on the existing finance screens rather than being
            rebuilt here. Linking out beats forking `groupIncome`. */}
        {can('finance.income') && (
          <Link className="c-an-nav__link c-an-nav__link--out" to={routes.income}>
            Revenue ↗
          </Link>
        )}
      </nav>

      <Outlet />
    </Page>
  );
}
